import {
  PHOTOCLEAR_OPERATIONS,
  PHOTOCLEAR_PRICING_VERSION,
  quotePhotoclearOperation,
  type PhotoclearOperationPrice,
} from '@rehub/pricing';
import { supabase } from './supabase';

export interface PhotoclearLivePrice {
  credits: number;
  updatedAt: string | null;
}

export type PhotoclearPriceMap = Record<string, PhotoclearLivePrice>;

export function cataloguePhotoclearPrices(): PhotoclearPriceMap {
  const prices: PhotoclearPriceMap = {};
  for (const action of PHOTOCLEAR_OPERATIONS) {
    prices[action.feature] = { credits: action.creditCost, updatedAt: null };
  }
  return prices;
}

export function creditsForOperation(operation: string, live: PhotoclearPriceMap): number | null {
  const quote = quotePhotoclearOperation(operation);
  if (!quote) return null;
  return live[quote.feature]?.credits ?? quote.creditCost;
}

export async function fetchPhotoclearPrices(): Promise<PhotoclearPriceMap> {
  const prices = cataloguePhotoclearPrices();
  const { data, error } = await supabase
    .from('credit_action_costs')
    .select('feature, credit_cost, updated_at, enabled')
    .eq('app', 'photoclear');
  if (error || !data) return prices;
  for (const row of data) {
    const feature = String(row.feature);
    if (!prices[feature] && feature !== 'declutter') continue;
    if (row.enabled === false) continue;
    const target = feature === 'declutter' ? 'declutter_light' : feature;
    if (!prices[target]) continue;
    prices[target] = {
      credits: Number(row.credit_cost),
      updatedAt: row.updated_at ?? null,
    };
  }
  return prices;
}

export type ReservePhotoclearResult =
  | { ok: true; creditCost: number; refId: string; alreadyReserved: boolean; balanceAfter: number | null; quote: PhotoclearOperationPrice }
  | { ok: false; error: 'UNKNOWN_ACTION' | 'STALE_QUOTE' | 'INSUFFICIENT_CREDITS' | 'ALREADY_COMPLETED' | 'ERROR'; creditCost?: number; message?: string };

export async function reservePhotoclearAction(input: {
  userId: string;
  operation: string;
  quotedCost: number;
  generationId?: string | null;
  refId?: string;
}): Promise<ReservePhotoclearResult> {
  const quote = quotePhotoclearOperation(input.operation);
  if (!quote) return { ok: false, error: 'UNKNOWN_ACTION' };
  const refId = input.refId || crypto.randomUUID();
  const { data, error } = await supabase.rpc('reserve_photoclear_action', {
    p_user_id: input.userId,
    p_feature: quote.feature,
    p_ref_id: refId,
    p_quoted_cost: input.quotedCost,
    p_generation_id: input.generationId ?? null,
  });
  if (error) {
    return { ok: false, error: 'ERROR', message: error.message };
  }
  const row = (data || {}) as {
    ok?: boolean;
    error?: string;
    status?: string;
    already_reserved?: boolean;
    credit_cost?: number;
    balance_after?: number;
  };
  if (row.error === 'STALE_QUOTE') {
    return { ok: false, error: 'STALE_QUOTE', creditCost: Number(row.credit_cost) };
  }
  if (row.error === 'UNKNOWN_ACTION') return { ok: false, error: 'UNKNOWN_ACTION' };
  if (row.error === 'INSUFFICIENT_CREDITS' || row.ok === false && row.error === 'INSUFFICIENT_CREDITS') {
    return { ok: false, error: 'INSUFFICIENT_CREDITS' };
  }
  if (row.ok === false) {
    const code = row.error === 'INSUFFICIENT_CREDITS' ? 'INSUFFICIENT_CREDITS' : 'ERROR';
    return { ok: false, error: code, message: row.error };
  }
  if (row.status === 'finalized') return { ok: false, error: 'ALREADY_COMPLETED' };
  if (row.status === 'released') return { ok: false, error: 'ERROR', message: 'This edit was already refunded.' };
  return {
    ok: true,
    creditCost: Number(row.credit_cost ?? input.quotedCost),
    refId,
    alreadyReserved: Boolean(row.already_reserved),
    balanceAfter: row.balance_after == null ? null : Number(row.balance_after),
    quote,
  };
}

export async function finalizePhotoclearAction(userId: string, refId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('finalize_credits', {
    p_user_id: userId,
    p_ref_type: 'photoclear_generation',
    p_ref_id: refId,
  });
  if (error) return false;
  return Boolean((data as { ok?: boolean } | null)?.ok);
}

export async function releasePhotoclearAction(userId: string, refId: string): Promise<number | null> {
  const { data, error } = await supabase.rpc('release_credits', {
    p_user_id: userId,
    p_ref_type: 'photoclear_generation',
    p_ref_id: refId,
  });
  if (error) return null;
  const row = data as { ok?: boolean; refunded?: number; already_released?: boolean } | null;
  if (!row?.ok) return null;
  return Number(row.refunded ?? 0);
}

export { PHOTOCLEAR_PRICING_VERSION };
